import Cookies from 'js-cookie';
import { logger } from './logger';

/**
 * Elimina absolutamente todo el estado del navegador asociado a la sesión:
 * todas las cookies accesibles vía JavaScript, `localStorage` y
 * `sessionStorage` completos. Se invoca al cerrar sesión para garantizar que
 * no quede ningún resto de la sesión anterior (tokens, datos de usuario,
 * estado persistido de Zustand, etc.).
 *
 * Nota: las cookies marcadas como `httpOnly` o de otro dominio no son
 * accesibles desde el cliente y no pueden eliminarse aquí; eso es trabajo del
 * backend / del flujo de expiración del token.
 */
export const clearBrowserStorage = (): void => {
  // Guard SSR: estas APIs solo existen en el navegador.
  if (typeof window === 'undefined') return;

  // 1. Cookies — eliminar todas las cookies accesibles vía JS.
  try {
    const allCookies = Cookies.get();
    Object.keys(allCookies).forEach((name) => {
      Cookies.remove(name, { path: '/' });
      Cookies.remove(name); // por si fue seteada sin path explícito
    });

    // Fallback manual: por si alguna cookie quedó con un path distinto a '/'.
    document.cookie.split(';').forEach((cookie) => {
      const name = cookie.split('=')[0]?.trim();
      if (!name) return;
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    });
  } catch (error) {
    const normalizedError = error instanceof Error ? error : new Error(String(error));
    logger.error('Error al limpiar cookies en logout:', normalizedError, { module: 'auth', action: 'clearCookies' });
  }

  // 2. localStorage — borrar todo.
  try {
    localStorage.clear();
  } catch (error) {
    const normalizedError = error instanceof Error ? error : new Error(String(error));
    logger.error('Error al limpiar localStorage en logout:', normalizedError, { module: 'auth', action: 'clearLocalStorage' });
  }

  // 3. sessionStorage — borrar todo.
  try {
    sessionStorage.clear();
  } catch (error) {
    const normalizedError = error instanceof Error ? error : new Error(String(error));
    logger.error('Error al limpiar sessionStorage en logout:', normalizedError, { module: 'auth', action: 'clearSessionStorage' });
  }
};
